// Click "everything about this place" panel: header, inline 12-month chart (this PLZ vs.
// reference for the active metric), and a table of every registry metric's value/ref/diff
// for the current month. See docs/DESIGN.md §6.2.
import type { MetricFile, PlzIndex, RegistryMetric, Registry } from "../registry";
import { difference, valueFor, type MonthSelection, type ReachValue } from "../logic";
import { renderMonthlyChart } from "./chart";
import { escapeHtml, formatDiff, formatValue } from "./format";

export interface DetailsPairwiseData {
  /** The registry's pairwise metric (e.g. "tt_transit") — may or may not be `activeMetric`. */
  metric: RegistryMetric;
  /** Decoded travel time from `refPlz` to the clicked PLZ, if the matrix loaded for this ref. */
  value: ReachValue | undefined;
  /** One-paragraph definition from the matrix's PairwiseMeta, shown in place of the 12-month
   * chart when the pairwise metric is the active one (it has no month dimension to chart). */
  definition: string;
}

export interface DetailsData {
  plz: string;
  refPlz: string;
  index: PlzIndex;
  registry: Registry;
  activeMetric: RegistryMetric;
  metricCache: Map<string, MetricFile>;
  month: MonthSelection;
  /** Present whenever the pairwise matrix is available, regardless of `activeMetric` — its
   * table row and (if active) its definition text are shown independently of the map metric. */
  pairwise?: DetailsPairwiseData | null;
}

function formatPairwiseCell(v: ReachValue | undefined): string {
  if (v === undefined || v === null) return "no data";
  if (v === "beyond") return "> 8 h";
  return `${Math.round(v)} min`;
}

export function renderDetails(container: HTMLElement, data: DetailsData, onSetReference: (plz: string) => void): void {
  const record = data.index[data.plz];
  const refRecord = data.index[data.refPlz];
  if (!record) {
    container.innerHTML = `<p>No data for PLZ ${data.plz}.</p>`;
    return;
  }

  const activeFile = data.metricCache.get(data.activeMetric.id);
  const dimSpec = data.activeMetric.dims.month;
  const isActivePairwise = data.activeMetric.kind === "pairwise";
  let chartSection = "";
  if (isActivePairwise) {
    const definition = data.pairwise?.metric.id === data.activeMetric.id ? data.pairwise.definition : "";
    chartSection = definition
      ? `<div class="details-definition">${escapeHtml(definition)}</div>`
      : `<p class="details-definition">Loading…</p>`;
  } else if (dimSpec && activeFile) {
    const plzValues = activeFile.values[data.plz] ?? [];
    const refValues = activeFile.values[data.refPlz] ?? [];
    const chart = renderMonthlyChart(plzValues, refValues, {
      unit: data.activeMetric.unit,
      plzLabel: `${data.plz} ${record.name}`,
      refLabel: `${data.refPlz} ${refRecord?.name ?? data.refPlz}`,
    });
    chartSection = `
      <div class="details-chart">
        ${chart}
        <div class="chart-legend">
          <span><span class="chart-swatch chart-swatch-plz"></span>${data.plz} ${escapeHtml(record.name)}</span>
          <span><span class="chart-swatch chart-swatch-ref"></span>${data.refPlz} (reference)</span>
        </div>
      </div>`;
  }

  const rows = data.registry.metrics
    .map((m) => {
      if (m.kind === "pairwise") {
        // Pairwise metrics have no per-PLZ MetricFile in metricCache — their value comes from
        // the reference's matrix row instead (main.ts decodes it once per render).
        if (!data.pairwise || data.pairwise.metric.id !== m.id) {
          return `<tr><th scope="row">${escapeHtml(m.short)}</th><td colspan="3" class="details-loading">Loading…</td></tr>`;
        }
        const cell = formatPairwiseCell(data.pairwise.value);
        return `<tr><th scope="row">${escapeHtml(m.short)}</th><td colspan="3">${cell} from ${escapeHtml(data.refPlz)} ${escapeHtml(refRecord?.name ?? "")}</td></tr>`;
      }
      const file = data.metricCache.get(m.id);
      // A metric not yet in the cache is still being fetched (see main.ts's
      // renderDetailsPanel) — that is a genuinely different state from "fetched but this
      // PLZ/month has no value", which formatValue renders as "no data".
      if (!file) {
        return `<tr><th scope="row">${escapeHtml(m.short)}</th><td colspan="3" class="details-loading">Loading…</td></tr>`;
      }
      const v = valueFor(file.values[data.plz], data.month, m.dims.month);
      const rv = valueFor(file.values[data.refPlz], data.month, m.dims.month);
      const d = difference(v, rv);
      return `<tr><th scope="row">${escapeHtml(m.short)}</th><td>${formatValue(v, m.decimals, m.unit)}</td><td>${formatValue(rv, m.decimals, m.unit)}</td><td>${formatDiff(d, m.decimals, m.unit)}</td></tr>`;
    })
    .join("");

  container.innerHTML = `
    <header class="details-header">
      <h2>${escapeHtml(record.name)} <span class="details-plz">${data.plz}</span></h2>
      <p class="details-meta">${escapeHtml(record.gemeinde)} · ${escapeHtml(record.canton)} · ${record.elev_ref} m elevation</p>
      <button type="button" class="set-ref-button">Set as reference</button>
    </header>
    ${chartSection}
    <table class="details-table">
      <thead><tr><th scope="col">Metric</th><th scope="col">This PLZ</th><th scope="col">Reference</th><th scope="col">Difference</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
  `;

  container.querySelector<HTMLButtonElement>(".set-ref-button")?.addEventListener("click", () => onSetReference(data.plz));
}
